import React, { useCallback, useState } from 'react';
import { debounce } from 'lodash';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Loader, Stack } from '@lyearn/ui';
import { leftPaddingInput } from '@/components/EditorBuilder/styles';
import TextInput from '@/components/EditorBuilder/TextInput';
import { Blocks, Features } from '@/components/PageEditor/types';
import WebsocketContainer from '@/components/WebsocketContainer';
import NotesTab from '@/modules/team-meetings/components/NotesTab';

import { useUpdateNoteMutation } from '../../graphql/mutations/generated/updateNote';
import { NoteFragment } from '../../graphql/queries/generated/notes';

const editorParams = { rootType: Blocks.SESSION_PAGE, featureType: Features.MEETING };

export const NotesView = ({
  note,
  setCurrentNote,
}: {
  note: NoteFragment;
  setCurrentNote: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
}) => {
  const rootBlockId = note?.rootBlockId;
  const [title, setTitle] = useState(note?.title);
  const { t } = useTranslation(['Common']);
  const [, updateNote] = useUpdateNoteMutation();

  const debouncedSetTitle = debounce((value: string) => {
    setTitle(value);
    updateNote({
      input: {
        id: note?.id,
        title: value,
      },
    });
  }, 1000);

  const onChange = useCallback(
    (newValue: string) => {
      debouncedSetTitle(newValue);
    },
    [debouncedSetTitle],
  );

  return (
    <React.Suspense fallback={null}>
      {rootBlockId ? (
        <Stack className="p-8 pb-24 w-full mx-auto" direction="col" gapY={24}>
          <Stack className="flex-shrink-0">
            <TextInput
              autoFocus
              className={cn('px-0', leftPaddingInput, 'text-text-primary me-32')}
              data-id="title"
              placeholder={t('Common:KnowledgeBase.UntitledNote')}
              value={title || ''}
              variant="heading-s"
              onChange={onChange}
              onPressEnter={() => {}}
            />
          </Stack>
          <WebsocketContainer>
            {({ onWebsocketDisconnect }) => (
              <NotesTab
                LoaderComponent={<Loader />}
                editorParams={editorParams}
                rootBlockId={rootBlockId}
                onWebsocketDisconnect={onWebsocketDisconnect}
              />
            )}
          </WebsocketContainer>
        </Stack>
      ) : null}
    </React.Suspense>
  );
};
